// Einstellungen, die dem GERÄT folgen statt dem Benutzer.
//
// Tim am 09./10.10.2026: "die aktuelle Auswahl, wie geöffnete Tabs etc.,
// so umbauen, dass diese nur für das jeweilige Gerät gelten. Dann kann auf
// dem iMac zwei Tabs nebeneinander und auf dem MacBook nur ein Tab in der
// Ansicht, weil der Bildschirm kleiner ist." Dem Gerät folgt "die
// anpassbare Oberfläche": die geöffneten und angeordneten Tabs, Hell/Dunkel
// und die Einrichtung der Leisten. Alles andere bleibt gemeinsam.
//
// Was ein Gerät ist, steht schon fest: ein Eintrag der Liste "Angemeldete
// Geräte" (Tabelle `devices`, erkannt am langlebigen Cookie). Der Server
// nennt die Kennung in /v2-api/auth/status (`device_id`).
//
// Gespeichert wird ohne neue Tabelle: derselbe Schlüssel, davor das Gerät
// -- `geraet:<id>:<schluessel>` in den Einstellungen des Benutzers. Der
// Server räumt diese Schlüssel ab, wenn das Gerät abgemeldet wird
// (internal/api/devices.go, dieselbe Schreibweise dort).
//
// Der nackte Schlüssel bleibt und wird bei jedem Schreiben mitgeführt: er
// ist die VORLAGE, der zuletzt benutzte Arbeitsplatz. Ein neues Gerät
// beginnt als Kopie davon und geht ab da seinen eigenen Weg. Eine Sitzung
// ohne Gerät (die Debug-Sitzung von scripts/agent-verify.sh) liest und
// schreibt nur die Vorlage -- sie fasst keinen Arbeitsplatz an.
//
// Bewusst ohne Laufzeit-Importe: die Regeln hier entscheiden, ob ein
// Arbeitsplatz erhalten bleibt, und sollen sich ohne Browser prüfen lassen
// (prefs-geraet.test.mjs). Den Speicher selbst führt prefs.svelte.ts.

/** Was dem Gerät folgt. Die EINE Liste -- wer hier etwas ergänzt, macht es
 *  auf jedem Gerät eigenständig (und beim nächsten Start aus der Vorlage
 *  übernommen). */
// shell_abschnitte (16.10.2026): Auf/Zu der Abschnitte im Baum -- der Platz
// ist auf dem MacBook ein anderer als auf dem iMac.
export const GERAETE_SCHLUESSEL = ["shell_layout", "shell_chrome", "theme_mode", "shell_abschnitte"] as const;

/** Was "Arbeitsplatz von Gerät X übernehmen" mitnimmt: Tabs und Leisten,
 *  NICHT Hell/Dunkel -- das hängt am Raum, in dem der Bildschirm steht. */
export const ARBEITSPLATZ_SCHLUESSEL = ["shell_layout", "shell_chrome", "shell_abschnitte"] as const;

const PRAEFIX = "geraet:";

/** Unter diesem localStorage-Schlüssel merkt sich der Browser, welches
 *  Gerät er ist -- NEBEN dem Einstellungs-Cache, nie darin: was im Cache
 *  steht, wandert als Einstellung zum Server und damit auf alle Geräte.
 *  Auch index.html liest ihn (Hell/Dunkel vor dem ersten Bild). */
export const GERAET_SPEICHER = "homelab-dash-geraet";

export function istGeraeteSchluessel(schluessel: string): boolean {
  return (GERAETE_SCHLUESSEL as readonly string[]).includes(schluessel);
}

/** `geraet:<id>:<schluessel>` -- dieselbe Schreibweise wie im Server. */
export function geraetSchluessel(geraet: number, schluessel: string): string {
  return `${PRAEFIX}${geraet}:${schluessel}`;
}

/** Zerlegt einen Geräte-Schlüssel; null, wenn es keiner ist. */
export function zerlege(schluessel: string): { geraet: number; schluessel: string } | null {
  if (!schluessel.startsWith(PRAEFIX)) return null;
  const rest = schluessel.slice(PRAEFIX.length);
  const i = rest.indexOf(":");
  if (i <= 0) return null;
  const geraet = Number(rest.slice(0, i));
  if (!Number.isInteger(geraet) || geraet <= 0) return null;
  return { geraet, schluessel: rest.slice(i + 1) };
}

type Lesen = (schluessel: string) => unknown;

/** Liest einen Wert für dieses Gerät: der eigene, sonst die Vorlage. Ohne
 *  Gerät gilt die Vorlage. */
export function liesFuerGeraet(lies: Lesen, geraet: number | null, schluessel: string): unknown {
  if (geraet == null) return lies(schluessel);
  const eigen = lies(geraetSchluessel(geraet, schluessel));
  return eigen !== undefined ? eigen : lies(schluessel);
}

/** Was ein Schreiben für dieses Gerät an den Server schickt: den eigenen
 *  Wert UND die Vorlage, in einem Zug. Ohne Gerät nur die Vorlage. */
export function schreibeFuerGeraet(
  geraet: number | null,
  schluessel: string,
  wert: unknown,
): Record<string, unknown> {
  if (geraet == null) return { [schluessel]: wert };
  return { [geraetSchluessel(geraet, schluessel)]: wert, [schluessel]: wert };
}

/** Übernahme beim ersten Kontakt: was diesem Gerät noch fehlt, bekommt es
 *  aus der Vorlage. Danach hat es eigene Werte und folgt keinem anderen
 *  mehr -- ohne das färbte ein Wechsel auf "dunkel" am iMac jedes Gerät
 *  mit, das selbst nie gewählt hat.
 *
 *  ⚠️ Nur nach einem GELUNGENEN Lesen vom Server aufrufen. "Im Cache steht
 *  kein eigener Wert" heißt sonst nicht "es gibt keinen" -- die Übernahme
 *  überschriebe den gemerkten Arbeitsplatz mit der Vorlage (dieselbe Falle
 *  wie am 21.08.2026, docs/shell.md). */
export function uebernahme(lies: Lesen, geraet: number | null): Record<string, unknown> {
  const patch: Record<string, unknown> = {};
  if (geraet == null) return patch;
  for (const k of GERAETE_SCHLUESSEL) {
    if (lies(geraetSchluessel(geraet, k)) !== undefined) continue;
    const vorlage = lies(k);
    if (vorlage !== undefined) patch[geraetSchluessel(geraet, k)] = vorlage;
  }
  return patch;
}

/** Schlüssel ANDERER Geräte, die der Server nicht (mehr) führt: sie fallen
 *  auch lokal weg. Der Cache lädt sonst alles hoch, was der Server nicht
 *  kennt -- und holte so den Arbeitsplatz eines abgemeldeten Geräts von
 *  jedem Browser zurück, der ihn noch gepuffert hat. */
export function verwaiste(
  lokal: Iterable<string>,
  server: Record<string, unknown>,
  geraet: number | null,
): string[] {
  const weg: string[] = [];
  for (const k of lokal) {
    const z = zerlege(k);
    if (!z || z.geraet === geraet) continue;
    if (!(k in server)) weg.push(k);
  }
  return weg;
}

/** Hat dieses Gerät einen gemerkten Arbeitsplatz, den man übernehmen kann? */
export function hatArbeitsplatz(lies: Lesen, geraet: number): boolean {
  return lies(geraetSchluessel(geraet, "shell_layout")) !== undefined;
}

/** "Arbeitsplatz von Gerät X übernehmen": der Patch, der Tabs und Leisten
 *  von `von` auf `nach` kopiert (und zur Vorlage macht). Leer, wenn dort
 *  nichts liegt oder beide dasselbe Gerät sind. */
export function arbeitsplatzVon(lies: Lesen, von: number, nach: number | null): Record<string, unknown> {
  const patch: Record<string, unknown> = {};
  if (nach == null || von === nach) return patch;
  for (const k of ARBEITSPLATZ_SCHLUESSEL) {
    const wert = lies(geraetSchluessel(von, k));
    if (wert === undefined) continue;
    Object.assign(patch, schreibeFuerGeraet(nach, k, wert));
  }
  return patch;
}

/** Liest die gemerkte Geräte-Kennung aus ihrem Text. */
export function leseGeraet(text: string | null | undefined): number | null {
  if (!text) return null;
  const n = Number(text);
  return Number.isInteger(n) && n > 0 ? n : null;
}
